import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Loader } from 'components/Shared/Loader'
import { ReadOnlyTextField } from 'components/Shared/ReadOnlyTextField'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import { downloadDriverFile, fetchDriver, getDriverFile } from 'services'
import { useErrorHandling } from 'context/errorHandling'
import { HTTP_404_ERROR } from 'Constants'
import { DetailsColumn, DetailsRow } from 'components/Shared/CrudComponents'
import { fetchContractor } from 'services/ContractorService'
import Sections from 'components/Drivers/sections'
import Module from 'constants/customFieldModules'
import { CustomFieldsProvider } from 'context/customFields'
import detailsStyles from './DriverDetails.module.css'
import { FilesPreviewDialog } from 'components/Shared/FilesPreviewDialog'
import { FileChip } from 'components/Shared/FileChip'
import {
  DRIVER_ADDITIONAL_DATA_READ,
  DRIVER_ATTACHMENTS_READ,
  DRIVER_CARRIER_READ, DRIVER_DATA_READ,
  DRIVER_EDIT,
  useAuth
} from 'context/auth'

export function DriverDetails (props) {
  const [driver, setDriver] = useState(null)
  const [carrier, setCarrier] = useState(null)
  const [previewedFile, setPreviewedFile] = useState(null)

  const driverId = props.match.params.id
  const fileId = props.match.params.fileId

  const { handleError } = useErrorHandling()
  const { t } = useTranslation()
  const { hasAnyPrivileges } = useAuth()
  const history = useHistory()

  useEffect(() => {
      fetchDriver(driverId)
        .then(setDriver)
        .catch((error) => {
        handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.driverNotFound'
          }
        ).then(() => history.push('/drivers'))
      })
    }, [driverId, handleError, history],
  )

  const hasCarrierReadPrivileges = hasAnyPrivileges([DRIVER_CARRIER_READ])
  useEffect(() => {
    if (driver?.carrierId && hasCarrierReadPrivileges) {
      fetchContractor(driver.carrierId)
        .then(setCarrier)
        .catch((error) => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.getCarrier'
          }
        ))
    }
  }, [driver, history, hasCarrierReadPrivileges, handleError])

  const editDriver = () => {
    history.push('/drivers/edit/' + driverId)
  }

  const setPreviewFile = (file) => history.push('/drivers/details/' + driver.id + '/files/' + file.id)

  useEffect(() => {
    const previewedAttachment = fileId ? driver?.files?.find(file => file.id === fileId) : null
    setPreviewedFile(previewedAttachment)
  }, [fileId, driver])

  const hasDriverDataReadPrivilege = hasAnyPrivileges([DRIVER_DATA_READ])
  const title = hasDriverDataReadPrivilege && driver ? driver.firstName + ' ' + driver.lastName : t('driver.details.title')
  return <div>
    {driver ?
      <>
        <TitleBar
          buttonLabel={t('driver.details.edit')}
          onButtonClick={editDriver}
          title={title }
          isButtonDisabled={!hasAnyPrivileges([DRIVER_EDIT])}
        />
        <CustomFieldsProvider module={Module.Drivers} entityId={driverId}>
          <DetailsRow>
            <DetailsColumn section={Sections.Carrier} visible={hasCarrierReadPrivileges}>
              <ReadOnlyTextField clearDisabled label={'driver.details.carrier'}
                                 value={carrier ? t(carrier.fullName) : ''}/>
            </DetailsColumn>
            <DetailsColumn section={Sections.BasicDetails} visible={hasDriverDataReadPrivilege}>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.firstName'} value={driver.firstName}/>
              <ReadOnlyTextField clearDisabled label={'driver.details.lastName'}
                                 value={driver.lastName}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.phoneNumber'} value={driver.phoneNumber}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.personalPhoneNumber'} value={driver.personalPhoneNumber}/>
              <ReadOnlyTextField clearDisabled label={'driver.details.address'}
                                 value={driver.address}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.idCardNumber'} value={driver.idCardNumber}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.idCardExpirationDate'}
                                 value={driver.idCardExpirationDate}
                                 type={'date'}/>
            </DetailsColumn>
            <DetailsColumn section={Sections.AdditionalDetails} visible={hasAnyPrivileges([DRIVER_ADDITIONAL_DATA_READ])}>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.adrCertificateNumber'}
                                 value={driver.adrCertificateNumber}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.adrCertificateExpirationDate'}
                                 value={driver.adrCertificateExpirationDate}
                                 type={'date'}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.driverLicenseNumber'}
                                 value={driver.driverLicenseNumber}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.driverLicenseCategory'}
                                 value={driver.driverLicenseCategory}/>
              <ReadOnlyTextField clearDisabled
                                 label={'driver.details.driverLicenseExpirationDate'}
                                 value={driver.driverLicenseExpirationDate}
                                 type={'date'}/>
            </DetailsColumn>
            <DetailsColumn section={Sections.Files} visible={hasAnyPrivileges([DRIVER_ATTACHMENTS_READ])}>
              <div className={detailsStyles.files}>
                <FilesPreviewDialog open={!!previewedFile}
                                    fileToShow={previewedFile}
                                    filesSource={getDriverFile}
                                    files={driver.files}
                                    onClose={() => {
                                      history.push('/drivers/details/' + driver.id)
                                    }}
                                    onFileChanged={setPreviewFile}
                />
                <ul className="d-flex flex-column p-0">
                  {driver.files.map(file => <FileChip
                    key={file.id}
                    file={file}
                    maxTextLength={30}
                    onDownload={() => downloadDriverFile(file)}
                    onShow={() => setPreviewFile(file)}
                  />)}
                </ul>
              </div>
            </DetailsColumn>
          </DetailsRow>
        </CustomFieldsProvider>
      </>
      :
      <Loader/>
    }
  </div>
}
